/** Shared contract + small building blocks for the per-template argument
 *  editors. Every editor is a controlled function of `rule.args` and reports
 *  complete new args objects via `onArgsChange` (never partial mutations) —
 *  the wire shapes are dashboard-compatible and documented in `ruleCatalog.ts`. */
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';

import type { ContextSchemaSource } from '@/components/TemplateCodeEditor/types';

import type { RuleDraft } from '../ruleTypes';

export interface ArgEditorProps {
  rule: RuleDraft;
  /** Replace the whole args object (null = no args). */
  onArgsChange: (args: Record<string, unknown> | null) => void;
  /** Patch rule columns outside args (`nested_ruleset`, expression fields). */
  onRuleChange: (patch: Partial<RuleDraft>) => void;
  /** Scope for tenant-bound pickers (tags, tours, rulesets). */
  tenantId: number | 'system';
  /** Ruleset being edited — excluded from the Include-ruleset picker. */
  excludeRulesetId?: number;
  disabled?: boolean;
  /** Event context schemas for DTL completions (notification editor only). */
  contextSchemas?: ContextSchemaSource[];
}

export function argsOf(rule: RuleDraft): Record<string, unknown> {
  return (rule.args ?? {}) as Record<string, unknown>;
}

/** String-code list read (dashboard stores enum codes as strings). */
export function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((v) => String(v)) : [];
}

/** The subset the args-only building blocks need (full ArgEditorProps is a
 *  superset, so editors can spread their props straight through). */
export interface ArgsOnlyProps {
  rule: RuleDraft;
  onArgsChange: (args: Record<string, unknown> | null) => void;
  disabled?: boolean;
}

/** Checkbox group over string-coded options → `args[argKey]: string[]`. */
export function CheckboxGroupArgs({
  rule,
  onArgsChange,
  disabled,
  argKey,
  options,
}: ArgsOnlyProps & { argKey: string; options: { value: string; label: string }[] }) {
  const selected = stringList(argsOf(rule)[argKey]);
  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onArgsChange({ ...argsOf(rule), [argKey]: next });
  };
  return (
    <FormGroup sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
      {options.map((o) => (
        <FormControlLabel
          key={o.value}
          control={
            <Checkbox
              size="small"
              checked={selected.includes(o.value)}
              onChange={() => toggle(o.value)}
              disabled={disabled}
            />
          }
          label={o.label}
        />
      ))}
    </FormGroup>
  );
}

/** Multi-select over string-coded options → `args[argKey]: string[]`. */
export function MultiSelectArgs({
  rule,
  onArgsChange,
  disabled,
  argKey,
  label,
  options,
}: ArgsOnlyProps & {
  argKey: string;
  label: string;
  options: { value: string; label: string }[];
}) {
  const selected = stringList(argsOf(rule)[argKey]);
  return (
    <FormControl size="small" sx={{ minWidth: 260 }} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={selected}
        renderValue={(vals) =>
          (vals as string[])
            .map((v) => options.find((o) => o.value === v)?.label ?? v)
            .join(', ')
        }
        onChange={(e) => {
          const v = e.target.value;
          onArgsChange({ ...argsOf(rule), [argKey]: typeof v === 'string' ? v.split(',') : v });
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            <Checkbox size="small" checked={selected.includes(o.value)} sx={{ py: 0, pl: 0 }} />
            {o.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/** One labelled checkbox bound to a boolean args key. */
export function BoolArg({
  rule,
  onArgsChange,
  disabled,
  argKey,
  label,
}: ArgsOnlyProps & { argKey: string; label: React.ReactNode }) {
  const args = argsOf(rule);
  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={Boolean(args[argKey])}
          onChange={(e) => onArgsChange({ ...args, [argKey]: e.target.checked })}
          disabled={disabled}
        />
      }
      label={label}
    />
  );
}
